import { FC, ReactElement, useEffect, useState } from 'react'
import cn from 'classnames'

import { constants } from 'config/constants'
import InputCode from 'components/app/input-code'

import { useTranslation } from 'utils/use-translation'

import ButtonBlue from '../button/_color/_blue'

import styles from './styles.module.scss'

interface Props {
  onSubmit: (code2fa: string) => void | Promise<void>
  isCodeValid: boolean
  isLoading: boolean
  setIsValidCode: (value: boolean) => void
  classNameInputCode?: string
  classNameButton?: string
  buttonName?: string
  buttonBack?: ReactElement
  errorCode?: string
  setErrorMsg?: (v: string) => void
  isAutoSubmitIgnored?: boolean
  disabled?: boolean
  isVisibleError?: boolean
  onChangeValue?: (value: string) => void
  errorRateLimitMsg?: string
  isRateLimitError?: boolean
  btnClassName?: string
  verificationInputClassName?: string
  labelClassName?: string
  className?: string
}

export const GoogleCheck2faCode: FC<Props> = ({
  onSubmit,
  isCodeValid,
  setIsValidCode,
  isLoading,
  disabled,
  classNameInputCode,
  classNameButton,
  buttonName,
  buttonBack,
  errorCode,
  setErrorMsg,
  isAutoSubmitIgnored,
  isVisibleError,
  onChangeValue,
  errorRateLimitMsg,
  isRateLimitError,
  btnClassName,
  verificationInputClassName,
  labelClassName,
  className,
}) => {
  const { t, locale, replacePlaceholders } = useTranslation()

  const [code, setCode] = useState('')
  const [isBtnDisabled, setIsBtnDisabled] = useState<boolean>(true)
  const [isBtnDisabledAfterConfirm, setIsBtnDisabledAfterConfirm] = useState<boolean>(null)
  const [isVisibleErrorState, setIsVisibleErrorState] = useState<boolean>(null)

  const handleOnSubmit = async () => await onSubmit(code)

  useEffect(() => {
    setIsBtnDisabledAfterConfirm(disabled)
  }, [disabled])

  useEffect(() => {
    setIsVisibleErrorState(isVisibleError)
  }, [isVisibleError])

  const handleOnChange = (value: string): void => {
    setCode(value)
    setIsValidCode(null)

    setIsBtnDisabled(value.length !== 6)
    if (!isAutoSubmitIgnored && value.length === constants.tfaCodeLength) {
      onSubmit(value)
    }

    setIsVisibleErrorState(false)
    setIsBtnDisabledAfterConfirm(false)
    setErrorMsg && setErrorMsg('')
    if (onChangeValue) {
      onChangeValue(value)
    }
  }

  const placeholders = {
    codeLength: constants.tfaCodeLength,
  }

  return (
    <div className={cn(styles.verification, className)}>
      <div className={cn(styles.verification__input, verificationInputClassName)}>
        <InputCode
          isValid={isCodeValid}
          value={code}
          onChange={handleOnChange}
          label={replacePlaceholders(t('enter_tfa_code'), placeholders)}
          onResendCode={null}
          hideResendText={true}
          maxLength={constants.tfaCodeLength}
          className={classNameInputCode}
          errorCode={errorCode}
          isVisibleError={isVisibleErrorState}
          labelClassName={labelClassName}
        />
      </div>

      <div className={cn(styles.verification__button, classNameButton)}>
        <small className={styles.error}>
          {isRateLimitError ? (locale === 'en' ? errorRateLimitMsg : t('rate_limit_exceeded_retry_later')) : ''}
        </small>
        <div className={styles.buttons}>
          {buttonBack && buttonBack}
          <ButtonBlue
            disabled={isBtnDisabled || isBtnDisabledAfterConfirm || isLoading}
            disabledHover={true}
            isLoading={isLoading}
            className={btnClassName}
            type="button"
            onClick={handleOnSubmit}
          >
            {buttonName || t('submit')}
          </ButtonBlue>
        </div>
      </div>
    </div>
  )
}
